import { useState, useEffect } from "react";
import {
  doc,
  deleteDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { uploadImage } from "../utils/uploadImage";
import { toast } from "../utils/toast";

export default function Admin() {
  // 🔐 AUTH STATE
  const [authorized, setAuthorized] = useState(false);
  const [password, setPassword] = useState("");

  // 🔥 STATES
  const [form, setForm] = useState({
    title: "",
    price: "",
    description: "",
    brand: "",
    category: "",
    image: null,
    active: true,
  });
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 10;

  useEffect(() => {
    const uniqueBrands = [
      ...new Set(products.map((p) => p.brand).filter(Boolean)),
    ];
    setBrands(uniqueBrands);
  }, [products]);

  // 🔐 CHECK AUTH (persist login)
  useEffect(() => {
    const savedAuth = localStorage.getItem("admin-auth");
    if (savedAuth === "true") {
      setAuthorized(true);
    }
  }, []);

  // 🔥 FETCH PRODUCTS ON LOAD
  useEffect(() => {
    if (authorized) fetchProducts();
  }, [authorized]);

  // 🔥 HANDLE EDITING PREFILL
  useEffect(() => {
    if (editingProduct) {
      setForm({
        title: editingProduct.title || "",
        price: editingProduct.price || "",
        description: editingProduct.description || "",
        brand: editingProduct.brand || "",
        category: editingProduct.category || "",
        image: null,
        active: editingProduct.active ?? true,
      });

      setPreview(editingProduct.image);
    }
  }, [editingProduct]);

  // 🔥 AUTH SCREEN
  if (!authorized) {
    return (
      <div style={container}>
        <div style={card}>
          <h2 style={{ color: "#fff" }}>Admin Login</h2>

          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />

          <button
            onClick={() => {
              if (password === process.env.REACT_APP_ADMIN_PASSWORD) {
                localStorage.setItem("admin-auth", "true");
                setAuthorized(true);
              } else {
                toast("Wrong password ❌", "error");
              }
            }}
            style={button}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  // 🔥 FETCH PRODUCTS
  const fetchProducts = async () => {
    const snapshot = await getDocs(collection(db, "products"));
    const data = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setProducts(data);
  };

  // 🔥 SUBMIT (ADD / EDIT)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.brand) return toast("Select or add a brand", "error");
    if (!form.category) return toast("Select category", "error");

    try {
      setLoading(true);

      let imageUrl = editingProduct?.image || "";

      if (form.image) {
        imageUrl = await uploadImage(form.image);
      }

      const productData = {
        title: form.title,
        price: Number(form.price),
        description: form.description,
        brand: form.brand,
        category: form.category,
        image: imageUrl,
        active: form.active,
        updatedAt: serverTimestamp(),
      };

      if (editingProduct) {
        await updateDoc(doc(db, "products", editingProduct.id), productData);
        toast("Product updated ✏️");
      } else {
        await addDoc(collection(db, "products"), {
          ...productData,
          createdAt: serverTimestamp(),
        });
        toast("Product added 🚀");
      }

      resetForm();
      fetchProducts();
    } catch (err) {
      console.error(err);
      toast("Error saving product ❌", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔥 DELETE
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;

    await deleteDoc(doc(db, "products", id));
    toast("Product deleted 🗑️");
    fetchProducts();
  };

  // 🔥 EDIT
  const startEdit = (product) => {
    setEditingProduct(product);
  };

  // 🔥 ARCHIVE
  const toggleActive = async (product) => {
    await updateDoc(doc(db, "products", product.id), {
      active: !product.active,
    });

    toast(product.active ? "Archived 📦" : "Restored ♻️");
    fetchProducts();
  };

  // 🔥 RESET
  const resetForm = () => {
    setForm({
      title: "",
      price: "",
      description: "",
      brand: "",
      category: "",
      image: null,
      active: true,
    });
    setEditingProduct(null);
    setPreview(null);
  };

  // 🔍 SEARCH
  const filteredProducts = products.filter((p) =>
    p.title.toLowerCase().includes(search.toLowerCase()),
  );

  // 📄 PAGINATION
  const indexOfLast = currentPage * productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfLast - productsPerPage,
    indexOfLast,
  );

  return (
    <div style={container}>
      <div style={card}>
        {/* 🔴 LOGOUT */}
        <button
          onClick={() => {
            localStorage.removeItem("admin-auth");
            window.location.reload();
          }}
          style={{
            marginBottom: 15,
            background: "red",
            color: "#fff",
            border: "none",
            padding: "6px 10px",
            borderRadius: 6,
          }}
        >
          Logout
        </button>

        <h2 style={{ color: "#fff" }}>
          {editingProduct ? "Edit Product" : "Admin Dashboard"}
        </h2>

        {/* FORM */}
        <form onSubmit={handleSubmit}>
          <input
            placeholder="Product Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            style={inputStyle}
          />

          <input
            type="number"
            placeholder="Price"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            style={inputStyle}
          />

          <select
            value={form.brand}
            onChange={(e) => setForm({ ...form, brand: e.target.value })}
            style={inputStyle}
          >
            <option value="">Select Brand</option>

            {brands.map((brand, index) => (
              <option key={index} value={brand}>
                {brand}
              </option>
            ))}
          </select>

          <input
            placeholder="Or type new brand (e.g. Tesla, Luminous...)"
            value={newBrand}
            onChange={(e) => {
              setNewBrand(e.target.value);
              setForm({ ...form, brand: e.target.value });
            }}
            style={inputStyle}
          />
          <button
            type="button"
            onClick={() => {
              if (!newBrand) return toast("Enter a brand name", "error");

              setBrands((prev) => [...new Set([...prev, newBrand])]);
              setForm({ ...form, brand: newBrand });
              setNewBrand("");
              toast("Brand added ✅");
            }}
            style={{
              marginBottom: 10,
              padding: "6px 10px",
              background: "#22c55e",
              color: "#fff",
              border: "none",
              borderRadius: 6,
            }}
          >
            Add Brand
          </button>

          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            style={inputStyle}
          >
            <option value="">Select Category</option>
            <option>Inverter</option>
            <option>Battery</option>
            <option>Panel</option>
          </select>

          <textarea
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            style={{ ...inputStyle, height: 100 }}
          />

          {/* IMAGE PREVIEW */}
          {preview && (
            <img
              alt="preview"
              src={preview}
              style={{
                width: "100%",
                height: 200,
                objectFit: "cover",
                borderRadius: 10,
                marginBottom: 10,
              }}
            />
          )}

          <input
            type="file"
            onChange={(e) => {
              const file = e.target.files[0];
              setForm({ ...form, image: file });

              if (file) {
                setPreview(URL.createObjectURL(file));
              }
            }}
            style={{ marginBottom: 10 }}
          />

          <label style={{ color: "#fff" }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Active
          </label>

          <button type="submit" disabled={loading} style={button}>
            {loading
              ? "Saving..."
              : editingProduct
                ? "Update Product"
                : "Add Product"}
          </button>
        </form>

        {/* SEARCH */}
        <input
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, marginTop: 20 }}
        />

        {/* PRODUCT LIST */}
        <div style={{ marginTop: 20 }}>
          {currentProducts.map((product) => (
            <div key={product.id} style={productCard}>
              <img src={product.image} alt="Product" style={imgStyle} />

              <div>
                <p style={{ color: "#fff" }}>{product.title}</p>
                <small style={{ color: "#94a3b8" }}>
                  {product.brand} • {product.category}
                </small>

                {!product.active && (
                  <p style={{ color: "red", fontSize: "12px" }}>Out of Stock</p>
                )}
              </div>

              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => startEdit(product)}>✏️</button>
                <button onClick={() => deleteProduct(product.id)}>🗑️</button>
                <button onClick={() => toggleActive(product)}>
                  {product.active ? "📦" : "♻️"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* PAGINATION */}
        <div style={{ marginTop: 15 }}>
          {Array.from({
            length: Math.ceil(filteredProducts.length / productsPerPage),
          }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                margin: 5,
                padding: "5px 10px",
                background: currentPage === i + 1 ? "#22c55e" : "#334155",
                color: "#fff",
                border: "none",
                borderRadius: 6,
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// 🎨 STYLES
const container = {
  minHeight: "100vh",
  background: "#0f172a",
  display: "flex",
  justifyContent: "center",
  padding: 40,
};

const card = {
  width: "100%",
  maxWidth: 700,
  background: "rgba(255,255,255,0.05)",
  padding: 20,
  borderRadius: 20,
};

const productCard = {
  display: "grid",
  gridTemplateColumns: "60px 1fr auto",
  gap: 10,
  alignItems: "center",
  background: "#1e293b",
  padding: 10,
  borderRadius: 10,
  marginBottom: 10,
};

const imgStyle = {
  width: 60,
  height: 60,
  objectFit: "cover",
  borderRadius: 8,
};

const inputStyle = {
  width: "100%",
  padding: 10,
  marginBottom: 10,
  borderRadius: 8,
};

const button = {
  width: "100%",
  padding: 12,
  background: "linear-gradient(135deg, #6366f1, #22c55e)",
  color: "#fff",
  border: "none",
  borderRadius: 10,
};
